import { createContext, useContext, useState } from "react"
import { animated, useTransition } from "react-spring"

import { twMerge } from "@lumen-energy/config-tailwind/twMerge"
import * as DialogPrimitive from "@radix-ui/react-dialog"

type DialogContext = { open: boolean; onLeaveAnimationFinish?: () => void }
const DialogContext = createContext<DialogContext>({ open: false })

export const Root = ({
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange: externalOnOpenChange,
  onClose,
  ...props
}: DialogPrimitive.DialogProps & { onClose?: () => void }) => {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <DialogContext.Provider
      value={{
        open: controlledOpen !== undefined ? controlledOpen : open,
        onLeaveAnimationFinish: onClose,
      }}
    >
      <DialogPrimitive.Root
        {...props}
        open={controlledOpen !== undefined ? controlledOpen : open}
        onOpenChange={(value) => {
          externalOnOpenChange?.(value)
          setOpen(value)
        }}
      />
    </DialogContext.Provider>
  )
}

export const Content = ({
  children,
  className,
  overlayClassName,
  position = "right",
  ...props
}: DialogPrimitive.DialogContentProps & {
  position?: "left" | "right" | "top"
  overlayClassName?: string
}) => {
  const { open, onLeaveAnimationFinish } = useContext(DialogContext)

  const transitionStyles = {
    right: {
      from: {
        opacity: 0,
        transform: "translateX(100%)",
      },
      enter: { opacity: 1, transform: "translateX(0%)" },
      leave: {
        opacity: 0,
        transform: "translateX(100%)",
      },
    },
    left: {
      from: {
        opacity: 0,
        transform: "translateX(-100%)",
      },
      enter: { opacity: 1, transform: "translateX(0%)" },
      leave: {
        opacity: 0,
        transform: "translateX(-100%)",
      },
    },
    top: {
      from: {
        opacity: 0,
        transform: "translateY(-100%)",
      },
      enter: { opacity: 1, transform: "translateY(0%)" },
      leave: {
        opacity: 0,
        transform: "translateY(-100%)",
      },
    },
  }[position]

  const transitions = useTransition(open, {
    ...transitionStyles,
    config: {
      clamp: true,
      tension: 300,
    },
    onDestroyed(value) {
      if (value) onLeaveAnimationFinish?.()
    },
  })

  return (
    <DialogPrimitive.Portal forceMount>
      {transitions((styles, item) =>
        item ? (
          <>
            <DialogPrimitive.Overlay
              className={twMerge(
                "fixed inset-0 isolate z-30 bg-black/50",
                overlayClassName,
              )}
              forceMount
              asChild
            >
              <animated.div style={{ opacity: styles.opacity }} />
            </DialogPrimitive.Overlay>

            <DialogPrimitive.Content
              forceMount
              asChild
              className={twMerge(
                "fixed top-0 isolate z-30 h-screen w-fit overflow-auto bg-white",
                {
                  "left-0": position === "left",
                  "right-0": position === "right",
                  "top-0": position === "top",
                },
                className,
              )}
              {...props}
            >
              <animated.div style={styles}>{children}</animated.div>
            </DialogPrimitive.Content>
          </>
        ) : null,
      )}
    </DialogPrimitive.Portal>
  )
}

export const Title = DialogPrimitive.Title
export const Description = DialogPrimitive.Description
export const Trigger = DialogPrimitive.Trigger
export const Close = DialogPrimitive.Close
